import type { PaymentKitEnvironment, PaymentKitErrors, PaymentKitStates, TInternalFuncs } from "../types";
import {
  definePaymentMethod,
  type FraudMetadataWire,
  getOrCreateCheckoutRequestId,
  toFraudMetadataWire,
} from "../utils";
import {
  type AddressPayload,
  clean,
  fromStripeAddress,
  type ShippingFields,
  shippingAddressFromFields,
} from "../utils/wallet-billing-address";
import {
  AirwallexApplePayAdapter,
  type AirwallexApplePayConfig,
  AirwallexApplePayMockScenario,
  type ApplePayEncryptedToken,
} from "./airwallex-apple-pay-adapter";
import { type BeforeConfirmOptions, runBeforeConfirm } from "./before-confirm";
import { splitName } from "./card";
import { handleNextAction } from "./next-action-handlers";
import { ApplePayMockScenario, StripeApplePayAdapter } from "./stripe-apple-pay-adapter";
import {
  createWalletSessionConfigReader,
  fetchWalletProcessors,
  resolveWalletProcessorType,
  type WalletProcessorInfo,
} from "./wallet-processors";
import { type CheckoutTrialInfo, walletTotalLabel } from "./wallet-trial";

// Apple Pay-specific types
export type ApplePayCustomerInfo = {
  first_name: string;
  last_name: string;
  email?: string;
  // A shipping address the shopper typed (the `shipping_*` submit fields). Wins over the sheet's.
  shipping_address?: AddressPayload;
};

export type ApplePayStartRequest = {
  processor_id: string;
  // Airwallex-only fields. Stripe /apple-pay/start is a pure read and
  // ignores these — customer_info/fraud_metadata are sent to /apple-pay/confirm
  // instead, where the customer + checkout attempt + PaymentIntent are created.
  customer_info?: ApplePayCustomerInfo;
  fraud_metadata?: FraudMetadataWire;
  mock_scenario?: string;
  // Airwallex-specific fields (sent on /start for merchant validation)
  validation_url?: string;
  initiative_context?: string;
  // Airwallex: the total the sheet was armed with from /session-info (PAY-1879).
  // /start is the write that finalizes the invoice, so it is where the backend
  // checks the armed total against the ledger and refuses (409) if it moved.
  // Only sent when the sheet total came from /session-info, never a
  // merchant-supplied amount.
  expected_amount?: number;
  expected_currency?: string;
};

export type ApplePayStartResponse = {
  amount: number;
  currency: string;
  country: string;

  // Checkout mode (PAY-1732): "setup" vaults the wallet payment method without
  // charging; "payment" charges. Read this instead of inferring setup mode from
  // amount === 0 — trials and fully discounted baskets are also $0 but are NOT
  // setup mode (PAY-1511). Absent on older backends, which were payment-only.
  mode?: "setup" | "payment";

  // Free trial (PAY-2341): the total is $0 today, so the sheet's total line
  // says what the trial is and what it costs afterwards. Absent when there is
  // no trial (and on older backends).
  trial?: CheckoutTrialInfo | null;

  // Stripe-specific (when calling /apple-pay/start)
  stripe_pk?: string;

  // Airwallex-specific (when calling /airwallex/apple-pay/start). Stripe's
  // /start does not create a checkout attempt — it's deferred to /confirm.
  checkout_attempt_id?: string;
  merchant_session?: { [key: string]: unknown }; // Apple merchant session for completeMerchantValidation()
};

// Airwallex 3DS next action type (matches backend snake_case response)
export type Airwallex3dsNextAction = {
  type: "airwallex_3ds";
  url: string;
};

export type ApplePayConfirmRequest = {
  // Stripe-only: moved from /start (where they used to drive customer/PI
  // creation). At confirm time, the Stripe path creates the customer, checkout
  // attempt, and PaymentIntent in a single call.
  processor_id?: string;
  customer_info?: ApplePayCustomerInfo;
  fraud_metadata?: FraudMetadataWire;
  // Stripe: Send PaymentMethod ID
  payment_method_id?: string;
  // Airwallex: Send encrypted Apple Pay token
  apple_pay_token?: ApplePayEncryptedToken | { [key: string]: unknown };
  // Email from payment sheet (Stripe Apple Pay) — used to fill missing customer email
  payer_email?: string;
  // Name from payment sheet — used to fill missing customer name
  payer_first_name?: string;
  payer_last_name?: string;
  // Billing address from the sheet (PAY-1953) — saved on the customer
  payer_billing_address?: AddressPayload;
  // Shipping address from the sheet, when asked for (requestShippingAddress)
  payer_shipping_address?: AddressPayload;
  // Stripe: the total the prepared sheet displayed (PAY-1879). The sheet is armed
  // net of customer credit, so the backend re-resolves at confirm and refuses
  // with 409 if the figure moved — never charging above what was authorized.
  // Optional: an older backend ignores it; the Airwallex confirm does not send it
  // (its /start already draws the credit, so there is nothing to drift).
  expected_amount?: number;
  expected_currency?: string;
  mock_scenario?: string;
};

export type ApplePayConfirmResponse = {
  charge_status: "success" | "fail" | "pending";
  transaction_id?: string;
  error_code?: string;
  error_message_for_customer?: string;
  error_message_for_debug?: string;
  checkout_attempt_id: string;
  checkout_session_id?: string;
  next_action?: Airwallex3dsNextAction; // Present when charge_status="pending" for 3DS
  payment_intent_id?: string;
  customer_id?: string;
  /** Customer name on file after confirm (best-effort; may be empty). */
  customer_first_name?: string;
  customer_last_name?: string;
  payment_method_id?: string;
  processor_used?: string;
  subscription_id?: string;
  invoice_id?: string;
  invoice_number?: number;
  card_brand?: string;
  card_last4?: string;
  card_exp_month?: number;
  card_exp_year?: number;
};

export type ApplePaySubmitOptions = BeforeConfirmOptions & {
  /** Processor external ID — optional when using auto-prepare (SDK fetches from checkout session) */
  processorId?: string;
  processorType?: "stripe" | "airwallex";
  customerInfo: ApplePayCustomerInfo;
  mockScenario?: ApplePayMockScenario;
  amount?: number; // Amount in atomic units (cents) for Apple Pay sheet display
  currency?: string; // Currency code (e.g., "usd")
  /** Country code — optional when using auto-prepare (fetched from backend) */
  country?: string;
  merchantName?: string; // Merchant display name on Apple Pay sheet
  /**
   * @internal Read by the SDK's own prepare only; `submit` ignores it. Use the
   * `requestShippingAddress` init option or `apple_pay.setRequestShippingAddress()`.
   */
  requestShippingAddress?: boolean;
};

type ApplePayResult =
  | { data: { [key: string]: unknown }; errors?: undefined }
  | { data?: undefined; errors: PaymentKitErrors };

// =============================================================================
// Prepared Payment State (for Stripe pre-fetching before click)
// =============================================================================

type PreparedStripeApplePayState = {
  processor: "stripe";
  adapter: StripeApplePayAdapter;
  startData: ApplePayStartResponse;
  mockScenarioStr?: string;
  checkoutRequestId: string;
  // PAY-1701: the processor whose publishable key was returned by /start and used
  // to MINT the account-scoped PaymentMethod in the browser. The confirm MUST charge
  // on this same processor (Stripe PMs are account-scoped), so it is pinned here and
  // reused at submit — never re-sourced from the submit options, which could diverge.
  processorId: string | undefined;
};

// Global state to store prepared Apple Pay data (Stripe only - Airwallex uses native session)
let preparedStripeState: PreparedStripeApplePayState | null = null;

// Prepared Airwallex state — persists options from prepare call for use at submit time
// The amount field is fetched from the session via /session-info endpoint to fix
// the $0.00 bug where options.amount was undefined and defaulted to 0.
type PreparedAirwallexState = {
  country: string;
  currency?: string;
  amount?: number;
  merchantName?: string;
  collectBillingAddress?: boolean;
  // PAY-2280: the device Apple Pay availability probe, run HERE at prepare time so the
  // tap -> `new ApplePaySession()` path stays synchronous. canMakePayments() is itself
  // synchronous, but awaiting the adapter's async wrapper inside the tap yields a
  // microtask that revokes the user gesture on slow paths (InvalidAccessError). Caching
  // it alongside the (already-cached) amount lets runAirwallexFlow construct the native
  // session with zero awaits after the tap — mirroring the Stripe/Google Pay prepared path.
  canMakePayment?: boolean;
};
let preparedAirwallexState: PreparedAirwallexState | null = null;

// Response type for Airwallex Apple Pay session-info endpoint
type AirwallexSessionInfoResponse = {
  amount: number;
  currency: string;
  country: string;
  // Absent on older backends: treat as true (PAY-1953).
  collect_billing_address?: boolean;
};

// =============================================================================
// Auto-Prepare State
// =============================================================================

type AutoPrepareConfig = {
  processorId: string;
  processorType: "airwallex" | "stripe" | undefined;
};

type CheckoutSessionConfig = {
  express_checkout_processor_id?: string | null;
  express_checkout_processor_type?: string | null;
};

// =============================================================================
// Apple Pay SDK Loader
// =============================================================================

// Loads Apple's JS SDK, which makes window.ApplePaySession available in non-Safari
// browsers (Chrome, Firefox, etc.) and enables the "Scan Code with iPhone" QR flow.
let applePayScriptLoadPromise: Promise<void> | null = null;
async function loadApplePayScript(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.ApplePaySession) return;
  if (document.getElementById("__pk_apple_pay_sdk")) {
    // Tag exists — if we have a cached promise, wait for it; otherwise the tag
    // already resolved (e.g. after a module reload) so return immediately.
    return applePayScriptLoadPromise ?? Promise.resolve();
  }
  applePayScriptLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.id = "__pk_apple_pay_sdk";
    script.src = "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
    script.crossOrigin = "anonymous";
    // NOTE: Apple doesn't publish stable SRI hashes for the 1.latest pointer,
    // so integrity checking is omitted. CSP must allow script-src https://applepay.cdn-apple.com.
    script.onload = () => resolve();
    script.onerror = () => {
      // Clear cached state so the next prepareApplePay call can retry.
      applePayScriptLoadPromise = null;
      document.getElementById("__pk_apple_pay_sdk")?.remove();
      reject(new Error("Failed to load Apple Pay SDK"));
    };
    document.head.appendChild(script);
  });
  return applePayScriptLoadPromise;
}

// =============================================================================
// Helper Functions
// =============================================================================

async function apiCall<T>(
  url: string,
  options: RequestInit,
  checkoutRequestId?: string,
): Promise<{ data?: T; error?: string }> {
  const headers = new Headers(options.headers);
  if (checkoutRequestId) {
    headers.set("x-request-id", checkoutRequestId);
  }

  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let errorMessage = `Request failed (${response.status})`;
    try {
      const errorData = await response.json();
      errorMessage = errorData.detail || errorMessage;
    } catch {
      errorMessage = response.statusText || errorMessage;
    }
    return { error: errorMessage };
  }
  return { data: await response.json() };
}

function validateOptions(options: ApplePaySubmitOptions): PaymentKitErrors | null {
  if (!options?.processorId) {
    return { processor_id: "Processor ID is required" };
  }
  // For Airwallex, country is required (either directly or from prepared state)
  if (options.processorType === "airwallex" && !options.country && !preparedAirwallexState?.country) {
    return { country: "Country is required for Airwallex Apple Pay" };
  }
  return null;
}

function getMockScenarioStr(mockScenario?: ApplePayMockScenario): string | undefined {
  return mockScenario && mockScenario !== ApplePayMockScenario.None ? mockScenario : undefined;
}

// =============================================================================
// Stripe Apple Pay Flow
// =============================================================================

async function callStripeStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  checkoutRequestId?: string,
): Promise<{ data?: ApplePayStartResponse; error?: string }> {
  // /apple-pay/start is now a pure read: it returns stripe_pk + sheet data
  // (amount/currency/country). Customer/CheckoutAttempt/PaymentIntent are
  // created in /apple-pay/confirm once the user authorizes the sheet.
  return apiCall<ApplePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/apple-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
      } as ApplePayStartRequest),
    },
    checkoutRequestId,
  );
}

function initializeStripeAdapter(
  stripePk: string,
  mockScenario?: ApplePayMockScenario,
): { adapter?: StripeApplePayAdapter; error?: string } {
  const adapter = new StripeApplePayAdapter(mockScenario);

  if (!adapter.initialize(stripePk)) {
    return { error: 'Stripe.js not loaded. Add <script src="https://js.stripe.com/v3/"></script> to your page.' };
  }

  return { adapter };
}

async function callStripeConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  paymentMethodId: string,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  // What /start armed the sheet with — echoed so the backend can detect drift.
  expectedSheet?: { amount: number; currency: string },
  payerFirstName?: string,
  payerLastName?: string,
  payerBillingAddress?: AddressPayload,
  payerShippingAddress?: AddressPayload,
): Promise<ApplePayResult> {
  // Merchant pre-charge validation (PAY-1540). The shopper has authorized in the
  // sheet but nothing has been charged yet — this is the last point where an
  // abort is free. Covers both Stripe entry points (prepared fast path and the
  // no-prepare fallback), which both funnel through here. On a block we return an
  // error result, so the caller's `paymentMethodEvent.complete("fail")` still
  // runs and the Apple sheet closes cleanly instead of hanging.
  const gate = await runBeforeConfirm(options.beforeConfirm, options.beforeConfirmTimeoutMs);
  if (!gate.proceed) {
    return { errors: { apple_pay: gate.error } };
  }

  // /apple-pay/confirm now carries the fields that used to live on /start —
  // processor_id, customer_info, fraud_metadata — because the customer,
  // checkout attempt, and Stripe PaymentIntent are all created here.
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/apple-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: toFraudMetadataWire(checkoutRequestId),
        payment_method_id: paymentMethodId,
        payer_email: payerEmail,
        payer_billing_address: payerBillingAddress,
        payer_shipping_address: payerShippingAddress,
        expected_amount: expectedSheet?.amount,
        expected_currency: expectedSheet?.currency,
        payer_first_name: payerFirstName,
        payer_last_name: payerLastName,
        mock_scenario: mockScenarioStr,
      } as ApplePayConfirmRequest),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return { errors: { apple_pay: result.error || "Failed to confirm payment" } };
  }

  const confirmData = result.data;

  if (confirmData.charge_status === "success") {
    return {
      data: {
        id: confirmData.transaction_id,
        checkoutAttemptId: confirmData.checkout_attempt_id,
        checkoutSessionId: confirmData.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: confirmData.payment_intent_id,
        customerId: confirmData.customer_id,
        customerFirstName: confirmData.customer_first_name,
        customerLastName: confirmData.customer_last_name,
        paymentMethodId: confirmData.payment_method_id,
        processorUsed: confirmData.processor_used,
        subscriptionId: confirmData.subscription_id,
        invoiceId: confirmData.invoice_id,
        invoiceNumber: confirmData.invoice_number,
        cardBrand: confirmData.card_brand,
        cardLast4: confirmData.card_last4,
        cardExpMonth: confirmData.card_exp_month,
        cardExpYear: confirmData.card_exp_year,
        errorCode: confirmData.error_code,
        errorMessageForCustomer: confirmData.error_message_for_customer,
        errorMessageForDebug: confirmData.error_message_for_debug,
        nextAction: confirmData.next_action,
      },
    };
  }

  return {
    errors: {
      apple_pay: confirmData.error_message_for_customer || confirmData.error_message_for_debug || "Payment failed",
    },
  };
}

// =============================================================================
// Prepare Function (call BEFORE the click handler)
// =============================================================================

/**
 * Pre-fetch Apple Pay data and prepare the PaymentRequest.
 * Call this when the checkout page loads, NOT in the click handler.
 *
 * For Stripe: pre-fetches data and prepares PaymentRequest for immediate show.
 * For Airwallex: returns success with processor="airwallex" so submit uses native ApplePaySession.
 *
 * @deprecated The SDK now calls this automatically on init. Use `onApplePayReady` to observe
 * readiness and `notifyAmountChanged` to trigger re-prepare after amount changes.
 */
export async function prepareApplePay(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  environment: PaymentKitEnvironment,
): Promise<{ success: boolean; error?: string; applePay?: boolean }> {
  console.log("[ApplePay] prepareApplePay called");

  // Clear any previous state
  preparedStripeState = null;

  const mockScenarioStr = getMockScenarioStr(options.mockScenario);
  const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
  console.log(`[ApplePay] Using checkout_request_id: ${checkoutRequestId}`);

  // Airwallex: Load Apple Pay SDK (enables QR code flow in non-Safari browsers),
  // then fetch session info to get the correct amount from line items.
  // This fixes the $0.00 bug where options.amount was undefined and defaulted to 0.
  if (options.processorType === "airwallex") {
    console.log("[ApplePay] Airwallex processor - loading Apple Pay SDK, fetching session info for amount");

    // Mock mode skips the real availability probe (jsdom has no ApplePaySession); treat
    // it as available. Hoisted out of the block below so it can be cached on the prepared
    // state and reused at submit time without re-probing inside the user gesture (PAY-2280).
    let canMakePayment = true;
    // In mock mode, skip SDK loading and availability check (jsdom has no ApplePaySession)
    if (!mockScenarioStr) {
      try {
        await loadApplePayScript();
      } catch (err) {
        console.error("[ApplePay] Failed to load Apple Pay SDK:", err);
        return { success: false, error: "Failed to load Apple Pay" };
      }

      canMakePayment = false;
      try {
        canMakePayment = !!window.ApplePaySession?.canMakePayments();
      } catch {
        canMakePayment = false;
      }
      if (!canMakePayment) {
        console.log("[ApplePay] Apple Pay not available on this device/browser");
        return { success: false, error: "Apple Pay not available on this device" };
      }
    }

    // Fetch session info (amount, currency, country) from backend
    const sessionInfoResult = await callAirwallexSessionInfoEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    if (sessionInfoResult.error || !sessionInfoResult.data) {
      console.error("[ApplePay] Failed to fetch session info:", sessionInfoResult.error);
      return { success: false, error: sessionInfoResult.error || "Failed to fetch Apple Pay session info" };
    }

    console.log("[ApplePay] Got session info:", sessionInfoResult.data);
    const resolvedAmount = sessionInfoResult.data.amount;
    const resolvedCurrency = sessionInfoResult.data.currency;
    const resolvedCountry = sessionInfoResult.data.country || options.country;

    preparedAirwallexState = {
      country: resolvedCountry,
      currency: resolvedCurrency,
      amount: resolvedAmount,
      merchantName: options.merchantName,
      collectBillingAddress: sessionInfoResult.data.collect_billing_address,
      // Cache the availability probe so submit never awaits it inside the tap (PAY-2280).
      canMakePayment,
    };
    console.log("[ApplePay] Airwallex prepared state:", preparedAirwallexState);
    return { success: true, applePay: true };
  }

  // Call Stripe start endpoint to fetch stripe_pk + sheet display data
  const startResult = await callStripeStartEndpoint(apiBaseUrl, secureToken, options, checkoutRequestId);
  if (startResult.error || !startResult.data) {
    return { success: false, error: startResult.error || "Failed to start Apple Pay" };
  }

  const startData = startResult.data;

  // Initialize Stripe adapter
  if (!startData.stripe_pk) {
    return { success: false, error: "Stripe publishable key not provided" };
  }

  const { adapter, error: adapterError } = initializeStripeAdapter(startData.stripe_pk, options.mockScenario);
  if (!adapter) {
    return { success: false, error: adapterError };
  }

  // Create PaymentRequest and call canMakePayment() (required before show())
  const prepareResult = await adapter.preparePaymentRequest({
    country: startData.country,
    currency: startData.currency.toLowerCase(),
    total: {
      label: walletTotalLabel(startData.trial),
      amount: startData.amount,
    },
    isSetupMode: startData.mode === "setup",
    requestShipping: options.requestShippingAddress,
  });

  if (!prepareResult.success) {
    return {
      success: false,
      error: prepareResult.error || "Failed to prepare payment request",
      applePay: prepareResult.applePay,
    };
  }

  if (!prepareResult.applePay) {
    return {
      success: false,
      error: "Apple Pay is not available on this device or Stripe account",
      applePay: false,
    };
  }

  // Store the prepared state
  preparedStripeState = {
    processor: "stripe",
    adapter,
    startData,
    mockScenarioStr,
    checkoutRequestId,
    // Pin the processor that minted the PM so confirm charges on the SAME account.
    processorId: options.processorId,
  };

  console.log("[ApplePay] prepareApplePay success (Stripe), ready for click");
  return { success: true, applePay: true };
}

/**
 * Check if Apple Pay has been prepared and is ready.
 */
export function isApplePayPrepared(): boolean {
  return (preparedStripeState?.adapter.isPrepared() ?? false) || preparedAirwallexState !== null;
}

/**
 * Clear prepared state (call on unmount or when checkout changes).
 */
export function clearPreparedApplePay(): void {
  if (preparedStripeState) {
    preparedStripeState.adapter.clearPrepared();
  }
  preparedStripeState = null;
  preparedAirwallexState = null;
}

// =============================================================================
// Airwallex Apple Pay Flow
// =============================================================================

/**
 * Fetch session info (amount, currency, country) from the backend.
 * Called during prepareApplePay to get the correct amount BEFORE showing the payment sheet.
 * This fixes the $0.00 bug where options.amount was undefined and defaulted to 0.
 */
async function callAirwallexSessionInfoEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<{ data?: AirwallexSessionInfoResponse; error?: string }> {
  return apiCall<AirwallexSessionInfoResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/session-info`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );
}

async function callAirwallexStartEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  validationUrl: string,
  initiativeContext: string,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  expectedSheet?: { amount: number; currency: string },
): Promise<{ data?: ApplePayStartResponse; error?: string }> {
  return apiCall<ApplePayStartResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/start`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        processor_id: options.processorId,
        customer_info: options.customerInfo,
        fraud_metadata: toFraudMetadataWire(checkoutRequestId),
        validation_url: validationUrl,
        initiative_context: initiativeContext,
        expected_amount: expectedSheet?.amount,
        expected_currency: expectedSheet?.currency,
        mock_scenario: mockScenarioStr,
      } as ApplePayStartRequest),
    },
    checkoutRequestId,
  );
}

async function callAirwallexConfirmEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  applePayToken: ApplePayEncryptedToken,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  payerEmail?: string,
  payerFirstName?: string,
  payerLastName?: string,
  payerBillingAddress?: AddressPayload,
  payerShippingAddress?: AddressPayload,
): Promise<ApplePayConfirmResponse> {
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/confirm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        apple_pay_token: applePayToken,
        payer_email: payerEmail,
        payer_first_name: payerFirstName,
        payer_last_name: payerLastName,
        payer_billing_address: payerBillingAddress,
        payer_shipping_address: payerShippingAddress,
        mock_scenario: mockScenarioStr,
      } as ApplePayConfirmRequest),
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message_for_debug: result.error || "Failed to confirm payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

async function callAirwallexVerifyEndpoint(
  apiBaseUrl: string,
  secureToken: string,
  checkoutRequestId?: string,
): Promise<ApplePayConfirmResponse> {
  const result = await apiCall<ApplePayConfirmResponse>(
    `${apiBaseUrl}/api/checkout/${secureToken}/airwallex/apple-pay/verify`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
    checkoutRequestId,
  );

  if (result.error || !result.data) {
    return {
      charge_status: "fail",
      error_message_for_debug: result.error || "Failed to verify payment",
      checkout_attempt_id: "",
    };
  }

  return result.data;
}

function toApplePayResult(response: ApplePayConfirmResponse, secureToken: string): ApplePayResult {
  if (response.charge_status === "success") {
    return {
      data: {
        id: response.transaction_id,
        checkoutAttemptId: response.checkout_attempt_id,
        checkoutSessionId: response.checkout_session_id ?? secureToken,
        state: "checkout_succeeded",
        paymentIntentId: response.payment_intent_id,
        customerId: response.customer_id,
        customerFirstName: response.customer_first_name,
        customerLastName: response.customer_last_name,
        paymentMethodId: response.payment_method_id,
        processorUsed: response.processor_used,
        subscriptionId: response.subscription_id,
        invoiceId: response.invoice_id,
        invoiceNumber: response.invoice_number,
        cardBrand: response.card_brand,
        cardLast4: response.card_last4,
        cardExpMonth: response.card_exp_month,
        cardExpYear: response.card_exp_year,
        errorCode: response.error_code,
        errorMessageForCustomer: response.error_message_for_customer,
        errorMessageForDebug: response.error_message_for_debug,
        nextAction: response.next_action,
      },
    };
  }
  return {
    errors: {
      apple_pay: response.error_message_for_customer || response.error_message_for_debug || "Payment failed",
    },
  };
}

const MAX_USER_ACTIONS = 5;

/**
 * Run the Airwallex Apple Pay flow with 3DS loop support.
 *
 * Flow:
 * 1. Show native ApplePaySession, call /start for merchant validation
 * 2. On payment authorized, call /confirm with token
 * 3. If 3DS required, handle it and call /verify
 * 4. Loop if more 3DS needed
 */
async function runAirwallexFlow(
  apiBaseUrl: string,
  secureToken: string,
  options: ApplePaySubmitOptions,
  mockScenarioStr?: string,
  checkoutRequestId?: string,
  // The sheet total when it came from /session-info at prepare time (PAY-1879).
  // Echoed on /start so the backend can refuse if the ledger moved since.
  expectedSheet?: { amount: number; currency: string },
  // From /session-info at prepare time: whether the sheet asks for the postal
  // address. Undefined means ask (PAY-1953).
  preparedCollectBillingAddress?: boolean,
  // From prepareApplePay: the device Apple Pay availability probe, run before the tap so
  // this flow need not probe availability inside the gesture (PAY-2280).
  // Undefined on the no-prepare fallback, where we probe inline.
  preparedCanMakePayment?: boolean,
  // Ask the sheet for a shipping address. Built at tap time, so the caller has
  // already dropped it when the shopper typed one.
  requestShippingAddress?: boolean,
): Promise<ApplePayResult> {
  let armedSheet = expectedSheet;
  let collectBillingAddress = preparedCollectBillingAddress;
  // Convert mock scenario
  let airwallexMockScenario: AirwallexApplePayMockScenario = AirwallexApplePayMockScenario.None;
  if (options.mockScenario === ApplePayMockScenario.Success) {
    airwallexMockScenario = AirwallexApplePayMockScenario.Success;
  } else if (options.mockScenario === ApplePayMockScenario.Cancelled) {
    airwallexMockScenario = AirwallexApplePayMockScenario.Cancelled;
  }

  // Defense-in-depth: validateOptions should catch this earlier, but guard against
  // future callers that bypass validation to avoid an unhandled TypeError on `.toUpperCase()`
  if (!options.country) {
    return { errors: { apple_pay: "Country is required for Airwallex Apple Pay" } };
  }

  // Resolve the sheet figures into LOCALS, never back onto `options`.
  // index.ts hands submitPayment the merchant's own options object by
  // reference, so writing the resolved amount onto it leaks into their next
  // call: a second submit() would see `amount` already set, skip the
  // /session-info refetch below, arm the STALE first-attempt total, and — since
  // the echo is only built when the amount came from us — send no
  // expected_amount at all. That switches the drift guard off on exactly the
  // retry where the ledger is most likely to have moved (PAY-1879).
  let sheetAmount = options.amount;
  let sheetCurrency = options.currency;
  let sheetCountry: string = options.country;

  // Auto-fetch amount from session if not provided (e.g. merchant didn't call prepareApplePay first)
  if (!sheetAmount) {
    const sessionInfoResult = await callAirwallexSessionInfoEndpoint(apiBaseUrl, secureToken, checkoutRequestId);
    if (!sessionInfoResult.data) {
      // The backend refuses a session whose total resolves to zero (e.g. fully
      // covered by customer credit). Surface that instead of falling through to
      // the `|| 0` fallback below, which on this branch has no amount to fall
      // back to and would arm a $0.00 sheet. (A merchant-supplied amount still
      // skips this whole block and is neither netted nor echoed — pre-existing,
      // tracked separately.)
      return { errors: { apple_pay: sessionInfoResult.error || "Failed to fetch Apple Pay session info" } };
    }
    sheetAmount = sessionInfoResult.data.amount;
    sheetCurrency = sheetCurrency || sessionInfoResult.data.currency;
    sheetCountry = sheetCountry || sessionInfoResult.data.country;
    collectBillingAddress = sessionInfoResult.data.collect_billing_address;
    // The sheet is about to show the backend's own figure: echo it on /start.
    armedSheet = { amount: sessionInfoResult.data.amount, currency: sessionInfoResult.data.currency };
  }

  // Initialize adapter
  const adapter = new AirwallexApplePayAdapter(airwallexMockScenario);

  const config: AirwallexApplePayConfig = {
    amount: sheetAmount || 0,
    currency: (sheetCurrency || "USD").toUpperCase(),
    country: sheetCountry.toUpperCase(),
    merchantName: options.merchantName || "Total",
    collectBillingAddress,
    requestShippingAddress,
  };

  if (!adapter.initialize(config)) {
    return { errors: { apple_pay: "Apple Pay not available on this device" } };
  }

  // Prefer the availability probed at prepare time (PAY-2280). The no-prepare fallback
  // probes inline, synchronously: when the merchant supplied `amount` the /session-info
  // fetch above is skipped, so an await here would be the only yield before the construct.
  const canPay = preparedCanMakePayment ?? adapter.canMakePaymentSync();
  if (!canPay) {
    return { errors: { apple_pay: "Apple Pay not available on this device" } };
  }

  // Get current domain for initiative_context
  const initiativeContext = typeof window !== "undefined" ? window.location.hostname : "";

  // In mock mode, call /start first to create checkout attempt (mock adapter skips merchant validation)
  if (airwallexMockScenario !== AirwallexApplePayMockScenario.None) {
    const startResult = await callAirwallexStartEndpoint(
      apiBaseUrl,
      secureToken,
      options,
      "https://mock-apple-pay-gateway.example.com/paymentservices/startSession",
      initiativeContext,
      mockScenarioStr,
      checkoutRequestId,
      armedSheet,
    );
    if (startResult.error || !startResult.data) {
      return { errors: { apple_pay: startResult.error || "Failed to start mock checkout" } };
    }
  }

  // Show Apple Pay sheet with merchant validation callback
  const paymentResult = await adapter.showPaymentSheet(async (validationUrl: string) => {
    // Call /start endpoint for merchant validation
    const startResult = await callAirwallexStartEndpoint(
      apiBaseUrl,
      secureToken,
      options,
      validationUrl,
      initiativeContext,
      mockScenarioStr,
      checkoutRequestId,
      armedSheet,
    );

    if (startResult.error || !startResult.data) {
      return { error: startResult.error || "Merchant validation failed" };
    }

    if (!startResult.data.merchant_session) {
      return { error: "No merchant session returned from server" };
    }

    return { merchantSession: startResult.data.merchant_session };
  });

  if (!paymentResult.success) {
    if ("cancelled" in paymentResult && paymentResult.cancelled) {
      return { errors: { apple_pay: "Apple Pay cancelled by user" } };
    }
    return { errors: { apple_pay: paymentResult.error || "Apple Pay failed" } };
  }

  const { token, complete, payerEmail, payerFirstName, payerLastName, payerBillingAddress, payerShippingAddress } =
    paymentResult;

  // Merchant pre-charge validation (PAY-1540). The shopper has authorized but the
  // Airwallex token has not been charged yet, so aborting here costs nothing.
  // `complete("fail")` is mandatory before returning — the native sheet is still
  // open showing a spinner and only completePayment() dismisses it.
  const gate = await runBeforeConfirm(options.beforeConfirm, options.beforeConfirmTimeoutMs);
  if (!gate.proceed) {
    complete("fail");
    return { errors: { apple_pay: gate.error } };
  }

  // Call /confirm with the token
  let response = await callAirwallexConfirmEndpoint(
    apiBaseUrl,
    secureToken,
    token,
    mockScenarioStr,
    checkoutRequestId,
    payerEmail,
    payerFirstName,
    payerLastName,
    payerBillingAddress,
    payerShippingAddress,
  );

  // Handle 3DS loop
  let userActionCount = 0;
  while (response.charge_status === "pending" && response.next_action && userActionCount < MAX_USER_ACTIONS) {
    userActionCount++;

    // Handle 3DS challenge — Airwallex3dsNextAction is structurally identical to SDK's Airwallex3dsAction
    const nextAction = response.next_action as Airwallex3dsNextAction;
    const actionResult = await handleNextAction({ type: nextAction.type, url: nextAction.url });

    // Call verify endpoint
    const verifyResponse = await callAirwallexVerifyEndpoint(apiBaseUrl, secureToken, checkoutRequestId);

    // Check if another 3DS action is required
    if (verifyResponse.charge_status === "pending" && verifyResponse.next_action) {
      if (!actionResult.success) {
        console.log("[ApplePay:Airwallex] 3DS failed but retry available, continuing loop...");
      }
      response = verifyResponse;
      continue;
    }

    // No more actions - check final result
    if (!actionResult.success) {
      complete("fail");
      return {
        errors: {
          apple_pay:
            verifyResponse.error_message_for_customer ||
            verifyResponse.error_message_for_debug ||
            actionResult.error ||
            "3DS authentication failed",
        },
      };
    }

    // 3DS succeeded
    complete(verifyResponse.charge_status === "success" ? "success" : "fail");
    return toApplePayResult(verifyResponse, secureToken);
  }

  // Check for max attempts exceeded
  if (userActionCount >= MAX_USER_ACTIONS) {
    complete("fail");
    return { errors: { apple_pay: "Too many authentication attempts. Please try again." } };
  }

  // No 3DS required - complete with result
  complete(response.charge_status === "success" ? "success" : "fail");
  return toApplePayResult(response, secureToken);
}

// =============================================================================
// Main Submit Function
// =============================================================================

const defSubmitPayment = (
  states: PaymentKitStates,
  getAutoPrepareConfig: () => AutoPrepareConfig | null,
  onClearPrepared: () => void,
  // Called when a prepared Airwallex or Stripe attempt STARTS. Returns a function that, after an
  // attempt that did not succeed, clears readiness and re-runs the instance auto-prepare (same
  // path as notifyAmountChanged) so the NEXT tap is prepared again (PAY-2280). It is a no-op if
  // the instance was cleaned up while the attempt ran, so a torn-down instance never re-prepares.
  armReprepare: () => () => void,
  // Whether the sheet should ask for a shipping address right now (init option, or
  // the latest setRequestShippingAddress call).
  getRequestShippingAddress: () => boolean,
) => {
  const submitPayment: TInternalFuncs["submitPayment"] = async (fields, options) => {
    const { apiBaseUrl, secureToken, environment } = states;
    // A shipping address the shopper typed into the merchant's form rides on
    // customer_info, where the backend prefers it over the sheet's. Always a
    // copy: the processor backfill below must not write onto the merchant's own
    // options object either, or their next submit() would look merchant-pinned.
    const typedShipping = shippingAddressFromFields(fields as ShippingFields);
    const merchantOptions = options as ApplePaySubmitOptions;
    const applePayOptions: ApplePaySubmitOptions = {
      ...merchantOptions,
      customerInfo: typedShipping
        ? { ...merchantOptions.customerInfo, shipping_address: typedShipping }
        : merchantOptions.customerInfo,
    };
    // Paths that build the sheet at tap time (Airwallex, the no-prepare Stripe
    // fallback) skip asking when the shopper already typed one, in `fields` or in
    // customerInfo.shipping_address (same street-line rule). The prepared Stripe
    // sheet was built earlier; setRequestShippingAddress(false) controls it.
    const hasTypedShipping = !!clean(applePayOptions.customerInfo?.shipping_address?.line1);
    const requestShippingAddress = getRequestShippingAddress() && !hasTypedShipping;

    // PAY-1701: capture the merchant's EXPLICIT processor choice BEFORE the
    // auto-prepare backfill below masks it. This is the "which processor" the merchant
    // asked for — distinct from the express-checkout fallback filled in when omitted.
    // Used at the prepared branch to decide whether the already-minted sheet is on the
    // requested account or must be re-minted elsewhere.
    const merchantSpecifiedProcessorId = applePayOptions.processorId;
    // This instance's pinned wallet processor (if any). Used to detect when the module-level
    // preparedStripeState was clobbered by ANOTHER instance pinned to a different account.
    const instancePinnedProcessorId = states.processorIds?.applePay;

    // Fill in processorId/processorType from auto-prepare config if merchant didn't provide them
    const autoPrepareConfig = getAutoPrepareConfig();
    if (!applePayOptions.processorId && autoPrepareConfig?.processorId) {
      applePayOptions.processorId = autoPrepareConfig.processorId;
    }
    if (!applePayOptions.processorType && autoPrepareConfig?.processorType) {
      applePayOptions.processorType = autoPrepareConfig.processorType;
    }

    // Validate options
    const validationError = validateOptions(applePayOptions);
    if (validationError) {
      return { errors: validationError };
    }

    try {
      const mockScenarioStr = getMockScenarioStr(applePayOptions.mockScenario);
      const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
      console.log(`[ApplePay] Using checkout_request_id: ${checkoutRequestId}`);

      // Finding 2 (PAY-1701): preparedStripeState is a module-level global shared across ApplePay
      // instances. If THIS instance is pinned (processorIds.applePay) and the global prepared state
      // was minted for a DIFFERENT account (another instance clobbered it), do NOT reuse it — fall
      // through to the no-prepare path, which mints+charges on this instance's own processor.
      // Residual: two UNPINNED Apple Pay instances still share the global (pre-existing behavior);
      // pinned instances are now isolated. A full per-instance refactor of preparedStripeState is
      // tracked as tech debt.
      const preparedBelongsToThisInstance =
        !instancePinnedProcessorId || preparedStripeState?.processorId === instancePinnedProcessorId;
      if (preparedStripeState?.adapter.isPrepared() && !preparedBelongsToThisInstance) {
        console.warn(
          `[ApplePay] Prepared state is for a different processor (${preparedStripeState?.processorId}) than this ` +
            `instance's pinned processorIds.applePay (${instancePinnedProcessorId}); re-preparing on the pinned processor.`,
        );
      }

      // Check if we have prepared Stripe state that belongs to THIS instance
      if (preparedStripeState?.adapter.isPrepared() && preparedBelongsToThisInstance) {
        // PAY-1701: the PaymentMethod is minted in the browser on the pk that /start
        // returned for the PREPARED processor, and Stripe PMs are account-scoped — so
        // confirm MUST charge on that SAME processor. A divergent submit-time processorId
        // cannot retro-change the already-minted PM, and re-minting here would break Apple
        // Pay's synchronous-gesture requirement (the sheet must open within the tap). So we
        // reuse the minting processor for confirm and warn. The merchant selects the wallet
        // account by PREPARING on it (prepareApplePay({ processorId })), which now threads
        // one id through mint AND charge. Mirrors the Google Pay path.
        const mintProcessorId = preparedStripeState.processorId;
        if (merchantSpecifiedProcessorId && mintProcessorId && merchantSpecifiedProcessorId !== mintProcessorId) {
          console.warn(
            `[ApplePay] submit processorId ${merchantSpecifiedProcessorId} differs from the prepared/minting ` +
              `processor ${mintProcessorId} — charging on the prepared processor (the PaymentMethod was minted ` +
              `for it). To charge on a specific processor, prepare Apple Pay on it first.`,
          );
        }
        // Pin confirm to the minting processor so mint == charge (never diverge).
        if (mintProcessorId) {
          applePayOptions.processorId = mintProcessorId;
        }
        console.log("[ApplePay] Using prepared Stripe state for immediate sheet display");

        // The prepared PaymentRequest is single-use: every exit below clears it. Without a
        // re-prepare, the next tap after a cancel, a sheet error or a declined confirm finds
        // nothing prepared and the hosted button is dead (PAY-2280, same root cause as the
        // Airwallex branch). Armed BEFORE the sheet opens; never awaited inside the tap.
        const reprepareAfterFailure = armReprepare();

        // Show Apple Pay sheet IMMEDIATELY using the prepared PaymentRequest
        const paymentResultPromise = preparedStripeState.adapter.showPreparedPaymentSheet();
        const paymentResult = await paymentResultPromise;

        if (!paymentResult.success) {
          clearPreparedApplePay();
          reprepareAfterFailure();

          if ("cancelled" in paymentResult && paymentResult.cancelled) {
            return { errors: { apple_pay: "Apple Pay cancelled by user" } };
          }
          const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
          return { errors: { apple_pay: errorMessage } };
        }

        const { paymentMethodId, paymentMethodEvent, billingDetails, shippingAddress } = paymentResult;
        const stripeName = billingDetails?.name ? splitName(billingDetails.name) : undefined;

        // Confirm with backend using the PaymentMethod ID
        const confirmResult = await callStripeConfirmEndpoint(
          apiBaseUrl,
          secureToken,
          applePayOptions,
          paymentMethodId,
          mockScenarioStr,
          preparedStripeState.checkoutRequestId,
          billingDetails?.email,
          { amount: preparedStripeState.startData.amount, currency: preparedStripeState.startData.currency },
          stripeName?.firstName,
          stripeName?.lastName,
          fromStripeAddress(billingDetails?.address),
          shippingAddress,
        );

        // Complete Apple Pay UI based on result
        if (confirmResult.data) {
          paymentMethodEvent.complete("success");
          clearPreparedApplePay();
          return confirmResult;
        } else {
          paymentMethodEvent.complete("fail");
          clearPreparedApplePay();
          reprepareAfterFailure();
          return confirmResult;
        }
      }

      // Check if this is an Airwallex processor - route directly to Airwallex flow
      if (applePayOptions.processorType === "airwallex") {
        console.log("[ApplePay] Running Airwallex flow (processorType=airwallex)");
        // Merge prepare-time state into submit options so country/currency/amount/merchantName
        // are available even if the merchant only provided them at prepare time
        // Only a total that came from /session-info is echoed for the drift
        // check. A merchant-supplied `amount` is NOT netted and NOT echoed, so
        // on that path the sheet still shows the merchant's figure while
        // finalize charges net — the PAY-1879 shape, unchanged from before this
        // fix and not addressed by it. Tracked separately; do not read the
        // absence of an echo here as a decision that the path is safe.
        let expectedSheet: { amount: number; currency: string } | undefined;
        // Merge prepare-time state into a COPY. Mutating applePayOptions would
        // mutate the merchant's own object — it is `options` cast, not cloned —
        // and their next submit() would then look merchant-supplied: no
        // refetch, stale total, no echo (PAY-1879).
        let airwallexOptions = applePayOptions;
        if (preparedAirwallexState) {
          const preparedAmount = preparedAirwallexState.amount;
          const preparedCurrency = preparedAirwallexState.currency;
          // Both are always set together by the one writer (the /session-info
          // handler), but the type allows either to be absent. Echo only when
          // we have the pair: a half-filled echo would compare the server's
          // figure against itself and read like a check that is not one.
          if (applePayOptions.amount == null && preparedAmount != null && preparedCurrency != null) {
            expectedSheet = { amount: preparedAmount, currency: preparedCurrency };
          }
          airwallexOptions = {
            ...applePayOptions,
            country: applePayOptions.country ?? preparedAirwallexState.country,
            currency: applePayOptions.currency ?? preparedAirwallexState.currency,
            amount: applePayOptions.amount ?? preparedAirwallexState.amount,
            merchantName: applePayOptions.merchantName ?? preparedAirwallexState.merchantName,
          };
        }
        let airwallexResult: ApplePayResult | undefined;
        const reprepareAfterFailure = armReprepare();
        try {
          airwallexResult = await runAirwallexFlow(
            apiBaseUrl,
            secureToken,
            airwallexOptions,
            mockScenarioStr,
            checkoutRequestId,
            expectedSheet,
            preparedAirwallexState?.collectBillingAddress,
            preparedAirwallexState?.canMakePayment,
            requestShippingAddress,
          );
          return airwallexResult;
        } finally {
          // The prepared state is single-use (it carried THIS attempt's armed sheet total).
          // Without a re-prepare the next tap — after a cancel, a decline, a 3DS failure —
          // would land on the no-prepare fallback and await /session-info INSIDE the
          // gesture: the exact PAY-2280 InvalidAccessError. Flip ready off (so hosted /
          // merchant buttons gate) and re-prepare in the background, mirroring
          // notifyAmountChanged. A succeeded checkout is terminal; nothing to re-arm.
          preparedAirwallexState = null;
          const succeeded = airwallexResult !== undefined && !("errors" in airwallexResult && airwallexResult.errors);
          if (!succeeded) {
            reprepareAfterFailure();
          }
        }
      }

      // No prepared state for Stripe - call start endpoint
      const startResult = await callStripeStartEndpoint(apiBaseUrl, secureToken, applePayOptions, checkoutRequestId);

      if (startResult.error || !startResult.data) {
        return { errors: { apple_pay: startResult.error || "Failed to start Apple Pay" } };
      }

      const startData = startResult.data;

      // Stripe flow without prepared state - this may fail due to user gesture requirements
      console.warn("[ApplePay] Stripe flow without prepared state - may fail due to user gesture requirements");

      if (!startData.stripe_pk) {
        return { errors: { apple_pay: "Stripe publishable key not provided" } };
      }

      const { adapter, error: adapterError } = initializeStripeAdapter(
        startData.stripe_pk,
        applePayOptions.mockScenario,
      );
      if (!adapter) {
        return { errors: { apple_pay: adapterError || "Failed to initialize Stripe adapter" } };
      }

      // This will likely fail due to user gesture requirements
      const prepareResult = await adapter.preparePaymentRequest({
        country: startData.country,
        currency: startData.currency.toLowerCase(),
        total: { label: walletTotalLabel(startData.trial), amount: startData.amount },
        isSetupMode: startData.mode === "setup",
        requestShipping: requestShippingAddress,
      });

      if (!prepareResult.success || !prepareResult.applePay) {
        return { errors: { apple_pay: prepareResult.error || "Apple Pay not available" } };
      }

      const paymentResult = await adapter.showPreparedPaymentSheet();

      if (!paymentResult.success) {
        if ("cancelled" in paymentResult && paymentResult.cancelled) {
          return { errors: { apple_pay: "Apple Pay cancelled by user" } };
        }
        const errorMessage = "error" in paymentResult ? paymentResult.error : "Unknown error";
        return { errors: { apple_pay: errorMessage } };
      }

      const { paymentMethodId, paymentMethodEvent, billingDetails, shippingAddress } = paymentResult;
      const stripeName = billingDetails?.name ? splitName(billingDetails.name) : undefined;

      const confirmResult = await callStripeConfirmEndpoint(
        apiBaseUrl,
        secureToken,
        applePayOptions,
        paymentMethodId,
        mockScenarioStr,
        checkoutRequestId,
        billingDetails?.email,
        { amount: startData.amount, currency: startData.currency },
        stripeName?.firstName,
        stripeName?.lastName,
        fromStripeAddress(billingDetails?.address),
        shippingAddress,
      );

      if (confirmResult.data) {
        paymentMethodEvent.complete("success");
        return confirmResult;
      } else {
        paymentMethodEvent.complete("fail");
        return confirmResult;
      }
    } catch (error) {
      onClearPrepared();
      return { errors: { apple_pay: `Apple Pay error: ${error}` } };
    }
  };

  return submitPayment;
};

// =============================================================================
// Payment Method Definition
// =============================================================================

const ApplePayPaymentMethod = definePaymentMethod((paymentKitStates) => {
  const { apiBaseUrl, secureToken, environment } = paymentKitStates;

  // Per-instance auto-prepare state — isolated from other ApplePayPaymentMethod instances.
  // Note: preparedStripeState and preparedAirwallexState remain module-level globals (pre-existing
  // architectural constraint shared across the file). Full per-instance isolation for those fields
  // would require threading them through prepareApplePay, runAirwallexFlow, validateOptions, and
  // defSubmitPayment — tracked as tech debt.
  let autoPrepareConfig: AutoPrepareConfig | null = null;
  // Per-session Apple-Pay processors list, fetched once only when a `processorIds.applePay` pin
  // needs its family resolved (PAY-1701). Cached across re-prepare / notifyAmountChanged.
  let applePayProcessorsCache: WalletProcessorInfo[] | null = null;
  let onApplePayReadyCallbacks: ((isReady: boolean) => void)[] = [];
  let isApplePayReadyState = false;
  let autoPrepareInProgress = false;
  // Incremented by cleanup() so that in-flight prepares can detect staleness and skip writes.
  let cleanupGeneration = 0;
  // Stores pending retry args when notifyAmountChanged is called while a prepare is in flight.
  let pendingAutoPrepareArgs: { apiBaseUrl: string; secureToken: string; environment: PaymentKitEnvironment } | null =
    null;
  // Resolvers waiting for a queued retry to complete (for correct notifyAmountChanged await).
  let pendingPrepareResolvers: (() => void)[] = [];
  // The first prepare reuses PaymentKit's shared session fetch; every later prepare reads the
  // session fresh (PAY-2447).
  const readSessionConfig = createWalletSessionConfigReader(paymentKitStates._initialSessionConfig);
  // Whether the sheet asks for a shipping address. Starts from the init option;
  // setRequestShippingAddress() changes it (and re-prepares the Stripe sheet).
  let requestShippingAddress = paymentKitStates.requestShippingAddress === true;

  function instanceSetApplePayReady(ready: boolean): void {
    isApplePayReadyState = ready;
    for (const cb of onApplePayReadyCallbacks) cb(ready);
  }

  function instanceClearPreparedApplePay(): void {
    clearPreparedApplePay();
    instanceSetApplePayReady(false);
  }

  async function instanceAutoPrepare(apBase: string, sToken: string, env: PaymentKitEnvironment): Promise<void> {
    if (autoPrepareInProgress) {
      // A prepare is already in flight; queue a retry and return a promise that resolves after it.
      pendingAutoPrepareArgs = { apiBaseUrl: apBase, secureToken: sToken, environment: env };
      return new Promise<void>((resolve) => pendingPrepareResolvers.push(resolve));
    }
    autoPrepareInProgress = true;
    // Capture generation so we can detect if cleanup() is called while we await.
    const capturedGeneration = cleanupGeneration;
    instanceSetApplePayReady(false);

    try {
      const result = await readSessionConfig<CheckoutSessionConfig>(apiCall, apBase, sToken);

      if (cleanupGeneration !== capturedGeneration) return;

      if (result.error || !result.data) {
        console.warn("[ApplePay] Auto-prepare: failed to fetch session config:", result.error);
        return;
      }

      // PAY-1701: a merchant-supplied `processorIds.applePay` (PaymentKit init option) OVERRIDES
      // the backend express-checkout scan, so the merchant picks exactly which account both MINTS
      // (via /start's pk) and CHARGES (via /confirm) Apple Pay. Since prepare pins this id and
      // confirm reuses it, one id threads through the whole flow.
      const override = paymentKitStates.processorIds?.applePay;
      let processorId: string;
      let processorType: "stripe" | "airwallex" | undefined;

      if (override) {
        // Resolve the pinned processor's REAL family from the backend's Apple-Pay processors list
        // — NOT from the scanned default's type, which would mis-route an Airwallex pin into the
        // Stripe /start and 400. Cache the list per instance so re-prepare / notifyAmountChanged
        // don't refetch. Only fetched when a pin is set, so the default path stays zero-cost.
        if (!applePayProcessorsCache) {
          const list = await fetchWalletProcessors(apiCall, apBase, sToken, "apple-pay");
          if (cleanupGeneration !== capturedGeneration) return;
          if (!list) {
            instanceSetApplePayReady(false); // fetch failed (already warned) — stays not-ready, retries next prepare
            return;
          }
          applePayProcessorsCache = list;
        }
        const resolvedType = resolveWalletProcessorType(applePayProcessorsCache, override);
        if (!resolvedType) {
          // Fail closed: the pinned id is absent OR its family is unrecognized. Never assume Stripe.
          console.warn(
            `[ApplePay] Pinned processorIds.applePay '${override}' is not a usable Apple-Pay processor ` +
              "for this checkout session (unknown family or not eligible) — Apple Pay stays unavailable " +
              "(not mis-routed).",
          );
          instanceSetApplePayReady(false);
          return;
        }
        processorId = override;
        processorType = resolvedType;
      } else {
        // Unset → keep the express-checkout fallback (PaymentKit's hosted checkout never sets a
        // pin, so it is unaffected).
        const scanned = result.data.express_checkout_processor_id;
        if (!scanned) {
          console.log("[ApplePay] Auto-prepare: no express checkout processor configured");
          return;
        }
        processorId = scanned;
        const raw = result.data.express_checkout_processor_type;
        processorType = raw === "airwallex" || raw === "stripe" ? raw : undefined;
      }

      autoPrepareConfig = { processorId, processorType };

      const prepareResult = await prepareApplePay(
        apBase,
        sToken,
        { processorId, processorType, customerInfo: { first_name: "", last_name: "" }, requestShippingAddress },
        env,
      );

      if (cleanupGeneration !== capturedGeneration) return;

      // notifyAmountChanged() or setRequestShippingAddress() arrived mid-prepare:
      // this sheet has the OLD amount or shipping flag. Drop it and stay not-ready;
      // the queued retry in the finally below prepares the current one (as Google Pay).
      if (pendingAutoPrepareArgs) {
        instanceClearPreparedApplePay();
        return;
      }

      instanceSetApplePayReady(prepareResult.success && prepareResult.applePay === true);
    } catch (err) {
      if (cleanupGeneration === capturedGeneration) {
        console.error("[ApplePay] Auto-prepare failed:", err);
      }
    } finally {
      autoPrepareInProgress = false;
      const stale = cleanupGeneration !== capturedGeneration;
      const pending = pendingAutoPrepareArgs;
      const resolvers = pendingPrepareResolvers.splice(0);
      if (!stale && pending) {
        pendingAutoPrepareArgs = null;
        // Run the retry and resolve all callers that were waiting for it.
        instanceAutoPrepare(pending.apiBaseUrl, pending.secureToken, pending.environment).then(() => {
          for (const r of resolvers) r();
        });
      } else {
        pendingAutoPrepareArgs = null;
        for (const r of resolvers) r();
      }
    }
  }

  // Start auto-prepare immediately so the button is ready before first user interaction.
  instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
    console.error("[ApplePay] Unhandled init error:", err),
  );

  return {
    name: "apple_pay",
    externalFuncs: {
      /**
       * @deprecated The SDK now prepares automatically on init. Use `onApplePayReady` to observe
       * readiness and `notifyAmountChanged` to trigger re-prepare after amount changes.
       */
      prepareApplePay: (options: ApplePaySubmitOptions) =>
        prepareApplePay(apiBaseUrl, secureToken, options, environment),

      /**
       * Register a callback to be notified when Apple Pay becomes ready (or stops being ready).
       * Fires immediately with the current state, then on every state change.
       * Multiple registrations are all notified — none are silently overwritten.
       *
       * @example
       * paymentKit.apple_pay.onApplePayReady((isReady) => {
       *   applePayButton.disabled = !isReady;
       * });
       */
      onApplePayReady: (callback: (isReady: boolean) => void): void => {
        onApplePayReadyCallbacks.push(callback);
        // Fire immediately with current state so caller doesn't miss a completed prepare.
        callback(isApplePayReadyState);
      },

      /**
       * Notify the SDK that the payment amount has changed (e.g. coupon applied).
       * The SDK will disable Apple Pay, re-prepare with the new amount, then re-enable.
       * The returned promise resolves when re-prepare is fully complete.
       *
       * @example
       * await paymentKit.apple_pay.notifyAmountChanged();
       */
      notifyAmountChanged: (): Promise<void> => {
        instanceClearPreparedApplePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },

      /**
       * Turn the sheet's shipping-address request on or off. Use it when your own form
       * has the shipping address: stop asking the sheet, and pass the address in
       * `fields` on `submit` instead. The initial value is the `requestShippingAddress`
       * init option.
       *
       * A Stripe sheet is built before the tap, so a change re-prepares Apple Pay; it is
       * not ready (`onApplePayReady(false)`) until the returned promise resolves. A call
       * that does not change the value does nothing.
       *
       * @example
       * // Ask Apple Pay for the address only while the shopper has not typed one.
       * await paymentKit.apple_pay.setRequestShippingAddress(!form.hasShippingAddress());
       */
      setRequestShippingAddress: (required: boolean): Promise<void> => {
        // Coerce like the init option: only `true` turns it on (a plain-JS "false" must not).
        const next = required === true;
        if (next === requestShippingAddress) return Promise.resolve();
        requestShippingAddress = next;
        instanceClearPreparedApplePay();
        autoPrepareConfig = null;
        return instanceAutoPrepare(apiBaseUrl, secureToken, environment);
      },
    },
    internalFuncs: {
      submitPayment: defSubmitPayment(
        paymentKitStates,
        () => autoPrepareConfig,
        instanceClearPreparedApplePay,
        () => {
          const armedGeneration = cleanupGeneration;
          return () => {
            if (cleanupGeneration !== armedGeneration) return;
            instanceClearPreparedApplePay();
            instanceAutoPrepare(apiBaseUrl, secureToken, environment).catch((err) =>
              console.error("[ApplePay] Re-prepare after attempt failed:", err),
            );
          };
        },
        () => requestShippingAddress,
      ),
      cleanup: () => {
        cleanupGeneration++;
        onApplePayReadyCallbacks = [];
        isApplePayReadyState = false;
        autoPrepareConfig = null;
        applePayProcessorsCache = null;
        pendingAutoPrepareArgs = null;
        pendingPrepareResolvers = [];
        autoPrepareInProgress = false;
        clearPreparedApplePay();
      },
    },
  };
});

export { ApplePayMockScenario };

export default ApplePayPaymentMethod;
